import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { banAuctionBidder, unbanAuctionBidder } from 'api/campaigns';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Bidder } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface BanBidderDialogProps extends AlertDialog.AlertDialogRootProps {
    bidder: Bidder;
}

const BanBidderDialog = (props: BanBidderDialogProps) => {
    const queryClient = useQueryClient();
    const { children, bidder, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const auctionId = Number(campaign.auction_id);
    const { success, error } = useToast();

    const isBanned = bidder.status === 'banned';
    const banAction = isBanned ? 'Unban' : 'Ban';
    const banTitle = `${banAction} bidder`;
    const banDescription = isBanned
        ? 'This bidder will be allowed to perform normal bidding. Their previous bids will not be reinstated.'
        : 'All bids associated with this bidder will be forfeited and passed to the next hightest bidder. This action cannot be undone.';

    const { mutate: toggleBan } = useMutation({
        mutationFn: ({ id: bidderId, status }: Bidder) =>
            status !== 'banned'
                ? banAuctionBidder(campaignId, auctionId, bidderId)
                : unbanAuctionBidder(campaignId, auctionId, bidderId),

        onSuccess: bidder => {
            success(bidder.status === 'banned' ? 'Bidder banned.' : 'Bidder reinstated.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
            });
        },
        onError: (err: GivebutterAxiosError) => {
            error(err.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>{banTitle}</AlertDialog.Title>
                        <AlertDialog.Description>{banDescription}</AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => toggleBan(bidder)}>
                            {banAction}
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const BanBidderDialogTrigger = AlertDialog.Trigger;

const Root = BanBidderDialog;
const Trigger = BanBidderDialogTrigger;

export {
    Root,
    Trigger,
    //
    BanBidderDialog,
    BanBidderDialogTrigger,
};
